import {ChangeEvent, useState} from 'react'
import {BASE_URL} from '../../config'
import axios from 'axios'
import {handleAxiosError} from '../../../utils/helperFunction/_helper'
import toast from 'react-hot-toast'
import {useAuth} from '../core/Auth'
import {getUserByToken} from '../core/_requests'
import {useLocation, useNavigate} from 'react-router-dom'

const RESEND_OTP_URL = `${BASE_URL}/api/auth/resendOTP`
const VERIFY_LOGIN_URL = `${BASE_URL}/api/auth/verifyLoginOtp`

type VerifyLoginOtpResponse = {
  accessToken?: string
  refreshToken?: string
  message?: string
  status?: string
  statuscode?: number
  statusCode?: number
}

const OtpEnter = () => {
  const {saveAuth, setCurrentUser} = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [isLoading, setIsLoading] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [otp, setOtp] = useState<string>('')

  const dashboardUrl = '/analyze/dashboard'

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setOtp(event.target.value)
  }

  const onVerify = async () => {
    if (!otp) {
      return
    }

    try {
      setIsLoading(true)
      const {data: auth} = await axios.post<VerifyLoginOtpResponse>(VERIFY_LOGIN_URL, null, {
        params: {otp},
      })

      if (!auth.accessToken) {
        saveAuth(undefined)
        setCurrentUser(undefined)
        toast.error(auth.message || 'Unable to verify OTP')
        return
      }

      saveAuth(auth)
      const {data: userInfo} = await getUserByToken(auth.accessToken)

      if (userInfo) {
        setCurrentUser({...userInfo})

        // Redirect to stored location or default to dashboard
        const redirectTo = location.state?.from || dashboardUrl
        navigate(redirectTo, {replace: true})
      } else {
        saveAuth(undefined)
        setCurrentUser(undefined)
        toast.error('Unable to load user information')
      }
    } catch (error) {
      saveAuth(undefined)
      setCurrentUser(undefined)
      const message = handleAxiosError(error)
      toast.error(message)
    } finally {
      setIsLoading(false)
    }
  }

  const onResendOtp = async () => {
    try {
      setOtp('')
      setIsResending(true)
      const {data} = await toast.promise(axios.post(RESEND_OTP_URL), {
        loading: 'Resending OTP',
        success: (response) => response.data?.message || 'OTP sent successfully',
        error: (error) => handleAxiosError(error),
      })

      return data
    } catch (error) {
      console.log(error)
    } finally {
      setIsResending(false)
    }
  }

  return (
    <div>
      <div className='mb-10'>
        <h3 className='text-center fs-1'>Two Factor Authentication</h3>
        <p className='text-center text-gray-600 fs-6 mb-0'>
          Enter the one-time code sent to your registered contact.
        </p>
      </div>
      <div className='fv-row'>
        {/* <label className='required form-label fs-6 text-dark'>Enter OTP</label> */}
        <input
          placeholder='Enter OTP'
          className='form-control bg-transparent custom-input'
          type='text'
          inputMode='numeric'
          autoComplete='off'
          autoFocus
          value={otp}
          onChange={handleOnChange}
        />
      </div>

      <button
        type='button'
        className='btn btn-link text-info fs-5 d-block mx-auto my-8 p-0'
        onClick={onResendOtp}
        disabled={isLoading || isResending}
      >
        {isResending ? 'Resending...' : 'Resend OTP'}
      </button>

      <div className='d-flex gap-4 flex-wrap justify-content-center'>
        <button
          type='button'
          className='btn btn-primary'
          disabled={!otp || isLoading}
          onClick={onVerify}
        >
          {!isLoading && <span className='indicator-label'>Verify</span>}
          {isLoading && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Please wait...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>

        <button
          type='button'
          className='btn btn-light'
          onClick={() => navigate('/auth/login', {replace: true})}
        >
          Back To Login
        </button>
      </div>
    </div>
  )
}

export default OtpEnter
